import { useEffect } from 'react';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Formik, useFormikContext } from 'formik';
import {
  getGetFeatureConfigsPublicQueryKey,
  getGetFeatureConfigsQueryKey,
  useCreateFeatureConfig,
  useUpdateSiteFeatureConfig,
} from '@juno/client-api';
import { FeatureConfig } from '@juno/client-api/utils';
import { EmailThemeConfigProps } from '@juno/constants';
import {
  ASPECT_RATIOS,
  JunoImageUpload,
  NavigationBlockerDialog,
  PageSelectorInput,
} from '@juno/ui';
import { MutationAction, onMutation, optimizeImage, useSettings } from '@juno/utils';
import {
  TABS as AUTOMATION_TABS,
  useAutomationToolbox,
} from '../Contexts/AutomationToolboxContext';
import { StyledFormControl, StyledImageInputBox } from '../Header/styles';
import ColorPicker from './ColorPicker';
import { SiteDesignContent, SiteDesignContentPanel } from './styles';

const ExternalLinkSchema = Yup.string()
  .max(128, 'URL must not exceed 128 characters')
  .test('title-valid-url', 'URL must begin with "http://" or "https://"', function test(value) {
    if (value && value.length > 0) {
      return value.startsWith('http://') || value.startsWith('https://');
    } else {
      return true;
    }
  });

export const EmailConfigSchema = Yup.object().shape({
  site_nickname: Yup.string().required('Site nickname is required'),
  primary_color: Yup.string().required('Primary color is required'),
  logo: Yup.string().required('Logo is required'),
  banner_image: Yup.string(),
  promo_banner_image: Yup.string(),
  contact_page_internal: Yup.boolean(),
  contact_page: Yup.string(),
  contact_page_url: Yup.string().when('contact_page_internal', {
    is: false,
    then: ExternalLinkSchema,
    otherwise: Yup.string().notRequired(),
  }),
  privacy_page_internal: Yup.boolean(),
  privacy_page: Yup.string(),
  privacy_page_url: Yup.string().when('privacy_page_internal', {
    is: false,
    then: ExternalLinkSchema,
    otherwise: Yup.string().notRequired(),
  }),
  discussions_page: Yup.string()
    .trim()
    .min(1, 'Discussions page is required')
    .required('Discussions page is required'),
  events_page: Yup.string()
    .trim()
    .min(1, 'Events page is required')
    .required('Events page is required'),
});

const SiteEmailDesignPannelInner = () => {
  const {
    brandSettingsDirty,
    setBrandSettingsDirty,
    brandSettingsValid,
    setBrandSettingsValid,
    currentTab,
    setCurrentTab,
    pendingTabChange,
    setPendingTabChange,
  } = useAutomationToolbox();

  const formik = useFormikContext<EmailThemeConfigProps>();

  useEffect(() => {
    setBrandSettingsValid(formik.isValid);
  }, [formik.isValid]);

  useEffect(() => {
    setBrandSettingsDirty(formik.dirty);
  }, [formik.dirty]);

  const handleBlockerConfirm = () => {
    formik.resetForm();

    requestAnimationFrame(() => {
      /* Formik has a delay to update the isValid and dirty states, 
      we need to wait for those values before change the tab */
      if (pendingTabChange) {
        setPendingTabChange(null);
        setCurrentTab(pendingTabChange);
      }
    });
  };

  const showNavigationBlocker =
    (brandSettingsDirty || !brandSettingsValid) && currentTab === AUTOMATION_TABS.SETTINGS;

  return (
    <SiteDesignContent>
      <NavigationBlockerDialog
        when={showNavigationBlocker}
        onConfirm={handleBlockerConfirm}
        onCancel={() => setPendingTabChange(null)}
        manualTrigger={showNavigationBlocker && pendingTabChange !== null}
      />
      <form onSubmit={formik.handleSubmit} noValidate>
        <SiteDesignContentPanel>
          <Grid container spacing={2}>
            <Grid item xs={6} py={1}>
              <Typography variant='subtitle1'>Site Nickname</Typography>
              <Typography variant='body2'>
                The name used for branding site email campaigns
              </Typography>
            </Grid>
            <Grid item xs={6} py={1} display='flex' justifyContent='flex-end'>
              <TextField
                label='Site Nickname'
                name='site_nickname'
                value={formik.values.site_nickname}
                onChange={formik.handleChange}
                size='small'
                disabled={formik.isSubmitting}
                fullWidth
                sx={{ width: 170 }}
                error={!!formik.errors.site_nickname}
                helperText={formik.errors.site_nickname}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6} py={1}>
              <Typography variant='subtitle1'>Primary color</Typography>
              <Typography variant='body2'>
                The main color used for branding site email campaigns
              </Typography>
            </Grid>
            <Grid item xs={6} py={1} display='flex' justifyContent='flex-end'>
              <ColorPicker
                label='Primary'
                color={formik.values.primary_color}
                colorName='primary.main'
                handleChange={(color) => formik.setFieldValue('primary_color', color)}
                disabled={formik.isSubmitting}
              />
              {!!formik.errors.primary_color && (
                <Typography fontSize={'12px'} align='left' color={'#d32f2f'}>
                  {formik.errors.primary_color}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant='subtitle1'>Logo</Typography>
              <Typography variant='body2'>
                Your logo appears at the very top of the email. If it appears too small or large,
                try cropping the image first.
              </Typography>
            </Grid>
            <Grid item xs={6} textAlign='right'>
              <StyledImageInputBox>
                <StyledFormControl>
                  <Box sx={{ width: 140 }}>
                    <JunoImageUpload
                      style={{ aspectRatio: '16/9', height: 70 }}
                      src={optimizeImage(270, formik.values.logo)}
                      srcUrl={formik.values.logo}
                      onFileUploaded={(selected) =>
                        formik.setFieldValue('logo', selected?.url || '')
                      }
                      iconInsteadOfText={true}
                      hoverForControls={true}
                      defaultAspectRatio={ASPECT_RATIOS.SIXTEEN_NINE}
                      imageType='Logo'
                    />
                    {!!formik.errors.logo && (
                      <Typography fontSize={'12px'} align='left' color={'#d32f2f'}>
                        {formik.errors.logo}
                      </Typography>
                    )}
                  </Box>
                </StyledFormControl>
              </StyledImageInputBox>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant='subtitle1'>Banner Image</Typography>
              <Typography variant='body2'>
                The banner image is displayed at the top of the email directly under your centered
                logo.
              </Typography>
            </Grid>
            <Grid item xs={6} textAlign='right'>
              <StyledImageInputBox>
                <StyledFormControl>
                  <Box sx={{ width: 140 }}>
                    <JunoImageUpload
                      style={{ aspectRatio: '25/8', height: 70 }}
                      src={optimizeImage(270, formik.values.banner_image)}
                      srcUrl={formik.values.banner_image}
                      onFileUploaded={(selected) =>
                        formik.setFieldValue('banner_image', selected?.url || '')
                      }
                      iconInsteadOfText={true}
                      hoverForControls={true}
                      defaultAspectRatio={ASPECT_RATIOS.TWENTY_FIVE_EIGHT}
                      imageType='Banner'
                    />
                  </Box>
                </StyledFormControl>
              </StyledImageInputBox>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant='subtitle1'>Promo Banner Image</Typography>
              <Typography variant='body2'>
                The Promo Banner image is displayed at the bottom of the email directly above the
                footer.
              </Typography>
            </Grid>
            <Grid item xs={6} textAlign='right'>
              <StyledImageInputBox>
                <StyledFormControl>
                  <Box sx={{ width: 140 }}>
                    <JunoImageUpload
                      style={{ aspectRatio: '4/1', height: 70 }}
                      src={optimizeImage(270, formik.values.promo_banner_image)}
                      srcUrl={formik.values.promo_banner_image}
                      onFileUploaded={(selected) =>
                        formik.setFieldValue('promo_banner_image', selected?.url || '')
                      }
                      iconInsteadOfText={true}
                      hoverForControls={true}
                      defaultAspectRatio={ASPECT_RATIOS.FOUR_ONE}
                      imageType='Promo Banner'
                    />
                  </Box>
                </StyledFormControl>
              </StyledImageInputBox>
            </Grid>
          </Grid>

          <PageSelectorInput<EmailThemeConfigProps>
            fieldId='discussions_page'
            label='Default Discussions Page'
            helperText='This page will be used as the default page for any global discussions links in your email campaigns.'
          />
          <PageSelectorInput<EmailThemeConfigProps>
            fieldId='events_page'
            label='Events Page'
            helperText='This page will be used as the default page for the View Events link in your email campaigns.'
          />
          <PageSelectorInput<EmailThemeConfigProps>
            fieldId='contact_page'
            fieldInternal='contact_page_internal'
            fieldUrl='contact_page_url'
            label='Contact Page'
            helperText='Your contact page will be accessible from a link in the email footer.'
            allowExternalUrl
          />
          <PageSelectorInput<EmailThemeConfigProps>
            fieldId='privacy_page'
            fieldInternal='privacy_page_internal'
            fieldUrl='privacy_page_url'
            label='Privacy Policy Page'
            helperText='Your privacy policy page will be accessible from a link in the email footer.'
            allowExternalUrl
          />

          <Stack direction='row' justifyContent='flex-end' spacing={2}>
            <LoadingButton
              aria-label='Save'
              variant='contained'
              type='submit'
              loading={formik.isSubmitting}
              disabled={formik.isSubmitting || !formik.dirty || !formik.isValid}
              sx={{ marginLeft: 'auto' }}
            >
              Save
            </LoadingButton>
            <Button
              aria-label='Cancel'
              variant='outlined'
              type='button'
              disabled={formik.isSubmitting || !formik.dirty}
              onClick={() => {
                formik.resetForm();
              }}
              sx={{ marginLeft: 'auto' }}
            >
              Cancel
            </Button>
          </Stack>
        </SiteDesignContentPanel>
      </form>
    </SiteDesignContent>
  );
};

const SiteEmailDesignPanel = () => {
  const { site, configs } = useSettings();
  const { setIsInitialSetup } = useAutomationToolbox();

  // Query invalidation
  const queryClient = useQueryClient();
  const refetchConfigs = () => {
    const queryKey = getGetFeatureConfigsQueryKey(site?.id || '');
    const publicQueryKey = getGetFeatureConfigsPublicQueryKey(site?.id || '');
    queryClient.invalidateQueries({ queryKey: queryKey });
    queryClient.invalidateQueries({ queryKey: publicQueryKey });
  };

  // Mutations
  const configCreate = useCreateFeatureConfig(
    onMutation(MutationAction.CREATE, 'FeatureConfig', refetchConfigs),
  );
  const configUpdate = useUpdateSiteFeatureConfig(
    onMutation(MutationAction.UPDATE, 'FeatureConfig', refetchConfigs),
  );

  const emailConfig = FeatureConfig.getEmailConfigConfig(configs);
  const initialConfig: EmailThemeConfigProps = {
    logo: emailConfig?.logo || '',
    banner_image: emailConfig?.banner_image || '',
    primary_color: emailConfig?.primary_color || '#000000',
    site_nickname: emailConfig?.site_nickname || '',
    contact_page_internal: emailConfig?.contact_page_internal,
    contact_page_url: emailConfig?.contact_page_url || '',
    contact_page: emailConfig?.contact_page || '',
    privacy_page_internal: emailConfig?.privacy_page_internal,
    privacy_page_url: emailConfig?.privacy_page_url || '',
    privacy_page: emailConfig?.privacy_page || '',
    promo_banner_image: emailConfig?.promo_banner_image || '',
    discussions_page: emailConfig?.discussions_page,
    events_page: emailConfig?.events_page,
  };

  useEffect(() => {
    // If the email config is invalid from the backend, means that it is initial setup
    setIsInitialSetup(!EmailConfigSchema.isValidSync(initialConfig));
  }, []);

  return (
    <Formik
      initialValues={initialConfig}
      onSubmit={async (values) => {
        const data = FeatureConfig.buildEmailConfigPayload(values, configs);
        try {
          if (data.id) {
            await configUpdate.mutateAsync({ siteId: site?.id || '', configType: data.type, data });
          } else {
            await configCreate.mutateAsync({ siteId: site?.id || '', data });
          }
          setIsInitialSetup(false);
        } catch (e) {
          console.error(e);
        }
      }}
      validationSchema={EmailConfigSchema}
      validateOnMount
      validateOnChange
      enableReinitialize
    >
      <SiteEmailDesignPannelInner />
    </Formik>
  );
};

export default SiteEmailDesignPanel;
